<template>
  <div>
    <div
      v-if="step === 'getQr'"
      class="d-flex flex-column align-items-center justify-content-center"
    >
      <p class="text-warning">
        {{ $t('Click the get qr code button to reconnect') }}
      </p>
      <b-spinner
        v-if="getQrSpin"
      />
      <statistic-card-vertical
        v-else
        icon="RefreshCcwIcon"
        :statistic-title="$t('Get qr code')"
        color="success"
        class="cursor-pointer"
        :click="getQr"
        statistic=""
      />
    </div>
    <div
      v-else-if="step === 'waitQr'"
      class="d-flex flex-column align-items-center justify-content-center"
    >
      <q-r-code-image
        :qr="qr"
      />
      <ol
        v-if="account.type === 'whatsapp'"
        class="mt-2"
        style="line-height: 2"
      >
        <li>{{ $t("Open WhatsApp on your phone") }}</li>
        <li><span> {{ $t("Tap") }} <strong><span> {{ $t("Menu") }} <span>
          <img
            :src="require('@/assets/images/icons/whatsapp-menu.svg')"
          >
        </span></span></strong> {{ $t("or") }} <strong><span> {{ $t("go to Settings") }} <span><img
          :src="require('@/assets/images/icons/whatsapp-settings.svg')"
        >   </span></span></strong> {{ $t("and select Linked Devices") }} </span>
        </li>
        <li>{{ $t("Point your phone to this screen to capture the code") }}</li>
      </ol>
      <ol
        v-else
        class="mt-2"
        style="line-height: 2"
      >
        <li><span>{{ $t("Open Telegram on your phone") }}</span></li>
        <li><span>{{ $t('Go to Settings > Devices > Link Desktop Device') }}</span></li>
        <li><span>{{ $t("Point your phone at this screen to confirm login") }}</span></li>
      </ol>

    </div>
    <div
      v-else-if="step === 'receivingQrCode' || step === 'error'"
      class="d-flex flex-column align-items-center justify-content-center"
    >
      <qr-code-loader
        width="350"
        height="350"
      />
      <p class="mt-2">
        {{ $t("Receiving qr code") }}
      </p>
    </div>
    <validation-observer
      v-else-if="step === 'waitPassword'"
      ref="connectForm"
      #default="{ invalid }"
    >
      <b-form @submit.prevent="onSubmit">
        <b-row>
          <b-col cols="12">
            <!-- password -->
            <b-form-group
              label-for="password"
              :label="$t('Password')"
            >
              <validation-provider
                #default="{ errors }"
                name="password"
                vid="password"
                rules="required"
              >
                <b-input-group
                  class="input-group-merge"
                  :class="errors.length > 0 ? 'is-invalid' : null"
                >
                  <b-form-input
                    id="register-password"
                    v-model="form.password"
                    class="form-control-merge"
                    :type="passwordFieldType"
                    :state="errors.length > 0 ? false : null"
                    name="password"
                    :placeholder="hint"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      :icon="passwordToggleIcon"
                      class="cursor-pointer"
                      @click="togglePasswordVisibility"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <!-- submit -->
          <b-col>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="primary"
              :disabled="spin || invalid"
            >
              <b-spinner
                v-if="spin"
                small
              />
              {{ $t("Connect account") }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BRow,
  BCol,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BSpinner,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { required } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import StatisticCardVertical from '@core/components/statistics-cards/StatisticCardVertical.vue'
import QRCodeImage from '@/components/accounts/show/connectors/QRCodeImage.vue'
import QrCodeLoader from '../../../loaders/QrCodeLoader.vue'

export default {
  name: 'AccountQrCodeConnector',
  components: {
    QRCodeImage,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    QrCodeLoader,
    StatisticCardVertical,
    // validations
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  mixins: [togglePasswordVisibility],
  props: {
    account: {
      type: Object,
      default: () => {},
    },
    connection: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      step: 'getQr',
      getQrSpin: false,
      qr: '',
      form: {
        password: '',
      },
      hint: '············',
      spin: false,
      required,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  beforeDestroy() {
    this.clearGetQrCodeInterval()
  },
  created() {
    this.getQr()
  },
  methods: {
    onSubmit() {
      this.$refs.connectForm.validate().then(success => {
        if (success) {
          // set spinner to submit button
          this.spin = true
          // send connect request
          this.$http.post(`accounts/${this.account.id}/complete-login`, this.form).then(({ data }) => {
            if (data.step === 'error') {
              this.spin = false
              this.step = 'getQr'
              this.showErrorNotification(data.error)
            } else if (data.step !== 'success') {
              this.spin = false
            } else {
              this.$emit('onAccountConnected')
            }
          }).catch(() => {
            this.spin = false
          })
        }
      })
    },
    getQr() {
      this.clearGetQrCodeInterval()
      this.$emit('onConnectedStart')
      this.getQrSpin = true
      this.$http.get(`accounts/${this.account.id}/get-qr`, { params: { waitQrCodeOrLogin: true } }).then(({ data }) => {
        this.step = data.step
        this.qr = data.qr
        if (data.hint) {
          this.hint = data.hint
        }
        if (this.step === 'success') {
          this.$emit('onAccountConnected')
        } else {
          if (this.step !== 'waitPassword') {
            this.setGetQrCodeInterval()
          }
          this.getQrSpin = false
        }
      }).catch(() => {
        this.getQrSpin = false
      })
    },
    setGetQrCodeInterval() {
      if (this.interval === null) {
        this.interval = setInterval(() => {
          this.getQr()
        }, 10 * 1000)
      }
    },
    clearGetQrCodeInterval() {
      if (this.interval !== null) {
        clearInterval(this.interval)
        this.interval = null
      }
    },
  },
}
</script>
<style scoped>

</style>
